import { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Grid, IconButton, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Ingredient } from '../../../models/ingredient';
import { getIngredients } from '../../../api/ingredient-api';

export interface IngredientInputItem {
  key: string;
  ingredientId: string;
  name: string;
  quantity: string;
}

interface IngredientInputProps {
  value: IngredientInputItem[];
  onChange: (ingredients: IngredientInputItem[]) => void;
  availableIngredients?: Ingredient[];
}

let nextKey = 0;
export const createIngredientInputItem = (partial?: Partial<IngredientInputItem>): IngredientInputItem => ({
  key: String(nextKey++),
  ingredientId: '',
  name: '',
  quantity: '',
  ...partial,
});

const IngredientInput = ({ value, onChange, availableIngredients: externalIngredients }: IngredientInputProps) => {
  const [localIngredients, setLocalIngredients] = useState<Ingredient[]>([]);
  const [error, setError] = useState<string | null>(null);

  const ingredients = externalIngredients ?? localIngredients;

  useEffect(() => {
    if (externalIngredients) return;
    getIngredients()
      .then(setLocalIngredients)
      .catch(() => {
        setError('Nepodařilo se načíst ingredience');
      });
  }, [externalIngredients]);

  const handleAdd = () => {
    onChange([...value, createIngredientInputItem()]);
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleIngredientChange = (index: number, ingredient: Ingredient | string | null) => {
    const updated = [...value];
    if (ingredient === null) {
      updated[index] = { ...updated[index], ingredientId: '', name: '' };
    } else if (typeof ingredient === 'string') {
      updated[index] = { ...updated[index], ingredientId: '', name: ingredient };
    } else {
      updated[index] = { ...updated[index], ingredientId: ingredient.id, name: ingredient.name };
    }
    onChange(updated);
  };

  const handleQuantityChange = (index: number, quantity: string) => {
    const updated = [...value];
    updated[index] = { ...updated[index], quantity };
    onChange(updated);
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Ingredience
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {value.map((item, index) => (
        <Grid container spacing={1} key={item.key} sx={{ mb: 1 }} alignItems="center">
          <Grid item xs={6}>
            <Autocomplete
              freeSolo
              options={ingredients}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
              value={
                item.ingredientId ? ingredients.find((i) => i.id === item.ingredientId) || null : item.name || null
              }
              inputValue={item.name}
              onInputChange={(_, newValue) => {
                handleIngredientChange(index, newValue);
              }}
              onChange={(_, newValue) => {
                handleIngredientChange(index, newValue);
              }}
              renderInput={(params) => <TextField {...params} label="Ingredience" size="small" />}
              isOptionEqualToValue={(option, val) => option.id === (val as Ingredient).id}
              size="small"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Množství"
              size="small"
              fullWidth
              value={item.quantity}
              onChange={(e) => handleQuantityChange(index, e.target.value)}
            />
          </Grid>
          <Grid item xs={2}>
            <IconButton onClick={() => handleRemove(index)} size="small" color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button startIcon={<AddIcon />} onClick={handleAdd} size="small" sx={{ mt: 0.5 }}>
        Přidat ingredienci
      </Button>
    </Box>
  );
};

export default IngredientInput;
